import {Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges, ViewChild} from '@angular/core';
import {DraggingService} from '@application/services/dragging/dragging.service';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {ListDrawing} from '@presentation/components/drawing-list/list-drawing';
import {RepositionTooltipComponent} from '@presentation/components/reposition-tooltip/reposition-tooltip.component';
import {ProductionScheduleBuilderAspectRatio} from '@presentation/pages/texfab/production-schedule/add/plan/builder/production-schedule-builder-aspect-ratio';
import {ProductionScheduleColoredYarnSetErrorPopupComponent} from '@presentation/pages/texfab/production-schedule/add/plan/builder/production-schedule-colored-yarn-set/production-schedule-colored-yarn-set-error-popup/production-schedule-colored-yarn-set-error-popup.component';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {BaseComponent, DialogBuilderFactoryService, DialogType, TranslateService, Unit} from '@vdw/angular-component-library';
import {select as d3Select} from 'd3-selection';
import {cloneDeep, isEqual, isNil} from 'lodash-es';
import {ProductionSchedulePathOfColoredYarnSetNewComponent} from './production-schedule-path-of-colored-yarn-set/production-schedule-path-of-colored-yarn-set-new.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-production-schedule-colored-yarn-set]',
  templateUrl: './production-schedule-colored-yarn-set.component.html',
  styleUrls: ['./production-schedule-colored-yarn-set.component.scss']
})
export class ProductionScheduleColoredYarnSetComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  @Input() public productionScheduleColoredYarnSetIndex: number;
  @Input() public commercialUnit: Unit;
  @Input() public productionSchedulePhase: ProductionSchedulePhase;
  @Input() public productionScheduleBuilderHeight: number;
  @Input() public aspectRatio: ProductionScheduleBuilderAspectRatio;
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public isEditPathsEnabled: boolean;
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public isProductionScheduleDisplayOverview: boolean;
  @Output() public hoveredProductionSchedulePathComponent: EventEmitter<{
    coloredYarnSetComponent: ProductionScheduleColoredYarnSetComponent;
    pathComponent: ProductionSchedulePathOfColoredYarnSetNewComponent;
  }> = new EventEmitter<{
    coloredYarnSetComponent: ProductionScheduleColoredYarnSetComponent;
    pathComponent: ProductionSchedulePathOfColoredYarnSetNewComponent;
  }>();

  @Output() public productionSchedulePlanChanged: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('productionScheduleColoredYarnSetBackground') public productionScheduleColoredYarnSetBackground: ElementRef;

  private currentSubscription: Subscription;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly draggingService: DraggingService,
    private readonly translate: TranslateService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.initialiseProductionScheduleColoredYarnSetComponent();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('aspectRatio' in changes || 'productionSchedule' in changes || 'productionScheduleBuilderHeight' in changes) {
      this.setProductionScheduleColoredYarnSetAttributes();
    }
  }

  public onHoveredProductionSchedulePathComponent(productionSchedulePathComponent: ProductionSchedulePathOfColoredYarnSetNewComponent): void {
    this.hoveredProductionSchedulePathComponent.emit({
      coloredYarnSetComponent: this,
      pathComponent: productionSchedulePathComponent
    });
  }

  public onProductionSchedulePlanChanged(): void {
    this.productionSchedulePlanChanged.emit();
  }

  public getProductionScheduleColoredYarnSetLeft(): number {
    return this.elementRef.nativeElement.x.baseVal.value;
  }

  public getProductionScheduleColoredYarnSetWidth(): number {
    return this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.technicalWidthInDents * this.aspectRatio.width;
  }

  public canShowProductionScheduleColoredYarnSetErrorOverlay(): boolean {
    return isEqual(this.productionSchedulePhase, ProductionSchedulePhase.VERIFY) && !isNil(this.productionSchedulePathsOfColoredYarnSet.errors);
  }

  public onClickProductionScheduleColoredYarnSetErrorOverlay(event: MouseEvent): void {
    const targetDOMRect: DOMRect = (event.target as HTMLElement).getBoundingClientRect();

    this.dialogBuilderFactoryService
      .getBuilder()
      .withClass('reposition-tooltip')
      .openDialog(RepositionTooltipComponent, {
        component: ProductionScheduleColoredYarnSetErrorPopupComponent,
        errors: this.productionSchedulePathsOfColoredYarnSet.errors,
        targetDOMRect: {
          top: event.clientY,
          bottom: targetDOMRect.bottom,
          left: event.clientX,
          right: targetDOMRect.right
        }
      });
  }

  public trackByProductionSchedulePath = (index: number, productionSchedulePath: ProductionSchedulePath): number => productionSchedulePath.pathNumber;

  public hasPermissionToNewVerifyStep(): boolean {
    return this.currentSubscription?.hasPermission(Permission.TEXFAB_VERIFY_NEW);
  }

  private initialiseProductionScheduleColoredYarnSetComponent(): void {
    d3Select(this.elementRef.nativeElement)
      .datum(this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet)
      .on('dragenter', (event: DragEvent) => {
        const isDraggingOverProductionScheduleColoredYarnSet: boolean = d3Select(document.elementFromPoint(event.clientX, event.clientY)).classed('production-schedule-colored-yarn-set-background');

        if (
          (this.draggingService.getCurrentData() && 'quantity' in this.draggingService.getCurrentData()) ||
          (!this.isEditPathsEnabled &&
            this.draggingService.getCurrentData() &&
            isDraggingOverProductionScheduleColoredYarnSet &&
            this.productionSchedule.canAddProductionScheduleItemToColoredYarnSet(
              this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent,
              this.draggingService.getCurrentData()
            ))
        ) {
          this.renderer.addClass(this.productionScheduleColoredYarnSetBackground.nativeElement, 'highlight');
        }
      })
      .on('drop', (event: DragEvent) => {
        const isDraggingOverProductionScheduleColoredYarnSet: boolean = d3Select(document.elementFromPoint(event.clientX, event.clientY)).classed('production-schedule-colored-yarn-set-background');

        if (this.draggingService.getCurrentData() && 'quantity' in this.draggingService.getCurrentData()) {
          this.dialogBuilderFactoryService
            .getBuilder()
            .withClass('alert-dialog')
            .openAlertDialog({
              titleText: 'PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN',
              messageText: this.translate.instant('PRODUCTION_ORDER.DESIGN_LIBRARY.ORDER_LINES.CANNOT_ADD_QUANTITY', {quantityAmount: this.draggingService.getCurrentData().quantity}),
              type: DialogType.INFORMATION
            });

          this.renderer.removeClass(this.productionScheduleColoredYarnSetBackground.nativeElement, 'highlight');
        } else if (!this.isEditPathsEnabled && !isNil(this.draggingService.getCurrentData()) && isDraggingOverProductionScheduleColoredYarnSet) {
          const draggedItem: ListDrawing = cloneDeep(this.draggingService.getCurrentData());

          if (
            this.productionSchedule.canAddProductionScheduleItemToColoredYarnSet(
              this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent,
              this.draggingService.getCurrentData()
            )
          ) {
            this.productionSchedulePlan.addDrawingToColoredYarnSet(draggedItem, this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent);
          } else if (
            !this.productionSchedule.enoughWidthToAddProductionScheduleItemToColoredYarnSet(
              this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent,
              this.draggingService.getCurrentData()
            )
          ) {
            this.dialogBuilderFactoryService.getBuilder().withClass('alert-dialog').openAlertDialog({
              titleText: 'PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN',
              messageText: 'PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.NOT_ENOUGH_WIDTH',
              type: DialogType.INFORMATION
            });
          }

          this.renderer.removeClass(this.productionScheduleColoredYarnSetBackground.nativeElement, 'highlight');
        }
        d3Select('.drawing-drag-preview').remove();
      })
      .on('dragleave', () => {
        if (!this.isEditPathsEnabled) {
          this.renderer.removeClass(this.productionScheduleColoredYarnSetBackground.nativeElement, 'highlight');
        }
      });
  }

  private setProductionScheduleColoredYarnSetAttributes(): void {
    const productionScheduleColoredYarnSetElement = this.elementRef.nativeElement;

    this.renderer.setAttribute(productionScheduleColoredYarnSetElement, 'x', `${this.getProductionScheduleColoredYarnSetPosition()}`);
    this.renderer.setAttribute(productionScheduleColoredYarnSetElement, 'width', `${this.getProductionScheduleColoredYarnSetWidth()}`);
    this.renderer.setAttribute(productionScheduleColoredYarnSetElement, 'height', `${this.productionScheduleBuilderHeight}`);
  }

  private getProductionScheduleColoredYarnSetPosition(): number {
    return (this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent - 1) * this.aspectRatio.width;
  }
}
